import axios from 'axios';
import { getCurrentEnvironment } from '../config/environments';

// Get the current environment configuration
let currentEnv = getCurrentEnvironment();
let API_URL = currentEnv.apiUrl;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      const timeoutError = new Error('Connection timeout. Please check your network connection or try a different environment.');
      timeoutError.isTimeout = true;
      timeoutError.originalError = error;
      return Promise.reject(timeoutError);
    }

    // Handle network errors (no response from server)
    if (!error.response) {
      const networkError = new Error('Unable to connect to server. Please check if the server is running and try again.');
      networkError.isNetworkError = true;
      networkError.originalError = error;
      return Promise.reject(networkError);
    }

    if (error.response?.status === 401) {
      // Token expired or invalid - remove token but don't redirect
      // Let ProtectedRoute handle the redirect with returnUrl
      console.log('[API] 401 Unauthorized - Token invalid or expired');
      localStorage.removeItem('token');
      
      // Don't do hard redirect here - let React Router handle it
      // This preserves the current URL for returnUrl functionality
      // window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Environment Management
// ============================================================================

export const updateApiEnvironment = (environment) => {
  currentEnv = environment;
  API_URL = environment.apiUrl;
  api.defaults.baseURL = API_URL;
  console.log('[API] Environment updated to:', environment.name, API_URL);
};

export const getCurrentApiUrl = () => {
  return API_URL;
};

// Get base URL for uploads (removes /api suffix if present)
export const getUploadBaseUrl = () => {
  // API_URL is like "https://api.fpstak.gg/api" or "http://localhost:3001/api"
  // We need "https://api.fpstak.gg" or "http://localhost:3001"
  // Remove the trailing /api from the end
  if (API_URL.endsWith('/api')) {
    return API_URL.slice(0, -4); // Remove last 4 characters ("/api")
  }
  return API_URL;
};

// Build complete URL for screenshot/upload path
export const getImageUrl = (filePath) => {
  if (!filePath) return '';
  // filePath is like "/uploads/screenshot-xxx.png"
  const baseUrl = getUploadBaseUrl();
  return `${baseUrl}${filePath}`;
};

// ============================================================================
// Token Management
// ============================================================================

export const getToken = () => {
  return localStorage.getItem('token');
};

export const setToken = (token) => {
  localStorage.setItem('token', token);
};

export const clearToken = () => {
  localStorage.removeItem('token');
};

// ============================================================================
// Authentication
// ============================================================================

export const register = async (username, email, password, displayName) => {
  const response = await api.post('/auth/register', {
    username,
    email,
    password,
    displayName,
  });
  return response.data;
};

export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// ============================================================================
// Squads
// ============================================================================

export const createSquad = async (name, description, settings) => {
  const response = await api.post('/squads', { name, description, settings });
  return response.data;
};

export const getSquads = async () => {
  const response = await api.get('/squads');
  return response.data;
};

export const getSquad = async (squadId) => {
  const response = await api.get(`/squads/${squadId}`);
  return response.data;
};

export const getSquadMembers = async (squadId) => {
  const response = await api.get(`/squads/${squadId}/members`);
  return response.data;
};

export const addSquadMember = async (squadId, username) => {
  const response = await api.post(`/squads/${squadId}/members`, { username });
  return response.data;
};

export const removeSquadMember = async (squadId, userId) => {
  const response = await api.delete(`/squads/${squadId}/members/${userId}`);
  return response.data;
};

export const updateSquad = async (squadId, updates) => {
  const response = await api.put(`/squads/${squadId}`, updates);
  return response.data;
};

export const deleteSquad = async (squadId) => {
  const response = await api.delete(`/squads/${squadId}`);
  return response.data;
};

// ============================================================================
// Sessions
// ============================================================================

export const createSession = async (squadId, name, game, map, settings, metadata) => {
  const response = await api.post('/sessions', {
    squadId,
    name,
    game,
    map,
    settings,
    metadata,
  });
  return response.data;
};

export const getSession = async (sessionId) => {
  const response = await api.get(`/sessions/${sessionId}`);
  return response.data;
};

export const endSession = async (sessionId) => {
  const response = await api.put(`/sessions/${sessionId}/end`);
  return response.data;
};

export const pauseSession = async (sessionId) => {
  const response = await api.put(`/sessions/${sessionId}/pause`);
  return response.data;
};

export const resumeSession = async (sessionId) => {
  const response = await api.put(`/sessions/${sessionId}/resume`);
  return response.data;
};

export const getSessionHistory = async (squadId, limit = 50, offset = 0) => {
  const response = await api.get(`/sessions/squad/${squadId}/history`, {
    params: { limit, offset },
  });
  return response.data;
};

export const getActiveSessions = async () => {
  const response = await api.get('/sessions/active/me');
  return response.data;
};

export const deleteSession = async (sessionId) => {
  const response = await api.delete(`/sessions/${sessionId}`);
  return response.data;
};

// ============================================================================
// Screenshots
// ============================================================================

export const uploadScreenshot = async (
  file,
  sessionId,
  positionX,
  positionY,
  game,
  map,
  metadata,
  uploadTimeMs
) => {
  const formData = new FormData();
  formData.append('screenshot', file);
  formData.append('sessionId', sessionId);
  if (positionX !== undefined) formData.append('positionX', positionX);
  if (positionY !== undefined) formData.append('positionY', positionY);
  if (game) formData.append('game', game);
  if (map) formData.append('map', map);
  if (metadata) formData.append('metadata', JSON.stringify(metadata));
  if (uploadTimeMs !== undefined) formData.append('uploadTimeMs', Math.round(uploadTimeMs));

  const response = await api.post('/screenshots', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const testScreenshotUpload = async (file, profileId, originalSize) => {
  const formData = new FormData();
  formData.append('screenshot', file);
  formData.append('profileId', profileId);
  formData.append('originalSize', originalSize);

  const response = await api.post('/screenshots/test', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getScreenshot = async (screenshotId) => {
  const response = await api.get(`/screenshots/${screenshotId}`);
  return response.data;
};

export const getSessionScreenshots = async (sessionId, limit = 100, offset = 0) => {
  const response = await api.get(`/screenshots/session/${sessionId}`, {
    params: { limit, offset },
  });
  return response.data;
};

export const getLatestScreenshots = async (sessionId) => {
  const response = await api.get(`/screenshots/session/${sessionId}/latest`);
  return response.data;
};

export const deleteScreenshot = async (screenshotId) => {
  const response = await api.delete(`/screenshots/${screenshotId}`);
  return response.data;
};

export const deleteScreenshots = async (screenshotIds) => {
  const response = await api.post('/screenshots/bulk-delete', {
    screenshotIds,
  });
  return response.data;
};

export const updateScreenshotMetadata = async (screenshotId, updates) => {
  const response = await api.patch(`/screenshots/${screenshotId}`, updates);
  return response.data;
};

export default api;

